import React, { useEffect } from 'react';
import { Route, useLocation, useNavigate, useParams } from 'react-router-dom';

// Simple global listener registry to emulate history.listen from react-router v5
const navigationListeners = new Set();
function notifyNavigationListeners() {
  navigationListeners.forEach((listener) => {
    try {
      listener();
    } catch (_) {}
  });
}

function RouterInjector({ Component, route }) {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const { Component: _omit, routes, ...routeProps } = route || {};

  // Notify listeners on location change (emulates history.listen)
  useEffect(() => {
    notifyNavigationListeners();
  }, [location]);

  const historyShim = {
    navigate,
    push: (to) => {
      if (typeof to === 'string') {
        navigate(to);
        return;
      }
      const nextPathname = to.pathname || location.pathname;
      const nextSearch = to.search || '';
      const nextHash = to.hash || '';
      navigate(`${nextPathname}${nextSearch}${nextHash}`);
    },
    goBack: () => {
      navigate(-1);
    },
    listen: (listener) => {
      navigationListeners.add(listener);
      return () => navigationListeners.delete(listener);
    },
  };
  return (
    <Component
      location={location}
      history={historyShim}
      match={{ params }}
      routes={routes}
      route={routeProps}
    />
  );
}

export function routeWithSubRoutes(route, index) {
  const element = <RouterInjector Component={route.Component} route={route} />;
  if (route.routes && route.routes.length) {
    return (
      <Route key={`route-${index}`} path={route.path} element={element}>
        {route.routes.map((child, i) =>
          routeWithSubRoutes(child, `${index}-${i}`)
        )}
      </Route>
    );
  }
  return <Route key={`route-${index}`} path={route.path} element={element} />;
}

export function withRouter(Component) {
  return function WithRouter(props) {
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();
    const historyShim = {
      navigate,
      push: (to) => {
        if (typeof to === 'string') {
          navigate(to);
          return;
        }
        const nextPathname = to.pathname || location.pathname;
        const nextSearch = to.search || '';
        const nextHash = to.hash || '';
        navigate(`${nextPathname}${nextSearch}${nextHash}`);
      },
      goBack: () => {
        navigate(-1);
      },
      listen: (listener) => {
        navigationListeners.add(listener);
        return () => navigationListeners.delete(listener);
      },
    };
    return (
      <Component
        {...props}
        location={location}
        history={historyShim}
        match={{ params }}
      />
    );
  };
}
